import { useReducer } from 'react'
import defaults from '../constants/defaults'

export enum SortOrder {
  asc = 'asc',
  desc = 'desc',
}

export interface IRequestParams<T> {
  filters?: Partial<T>
  pageCount: number
  perPage: number
  sortField?: string
  sortOrder?: string
}

export interface IFiltersParams<T> extends IRequestParams<T> {
  currentPage: number
  pageTotalCount: number
  totalCount?: number
  showPagination: boolean
}

interface IActionType<T> {
  type: string
  field?: string
  value?: T
}

export const filterPageDefaults = {
  pageTotalCount: 0,
  currentPage: 1,
  pageCount: defaults.PAGE_COUNT,
}

export const defaultParams = {
  ...filterPageDefaults,
  perPage: defaults.PER_PAGE,
  filters: undefined,
  showPagination: false,
}

enum ParamActions {
  SET_FILTER = 'SET_FILTER',
  SET_ALL_FILTERS = 'SET_ALL_FILTERS',
  SET_FILTERS = 'SET_FILTERS',
  RESET_FILTER = 'RESET_FILTER',
  SET_PAGE_COUNT = 'SET_PAGE_COUNT',
  SET_PAGE_TOTAL = 'SET_PAGE_TOTAL',
  SET_SORT_ORDER = 'SET_SORT_ORDER',
  SET_SORT_FIELD = 'SET_SORT_FIELD',
  SET_SORT = 'SET_SORT',
}

const paramReducer = <T>(state: IFiltersParams<T>, action: IActionType<any>) => {
  const { field, type, value } = action

  switch (type) {
    case ParamActions.SET_FILTERS:
      return {
        ...state,
        ...filterPageDefaults,
        filters: {
          ...state.filters,
          ...value,
        },
      }
    case ParamActions.SET_ALL_FILTERS:
      return {
        ...state,
        ...filterPageDefaults,
        filters: {
          ...value,
        },
      }
    case ParamActions.SET_FILTER:
      if (!field) {
        throw new Error('Need a field value')
      }

      return {
        ...state,
        ...filterPageDefaults,
        filters: {
          [field]: value,
        },
      }
    case ParamActions.RESET_FILTER:
      return {
        ...defaultParams,
        perPage: state.perPage,
      }
    case ParamActions.SET_PAGE_COUNT: {
      const pageCount = value - 1

      return {
        ...state,
        pageCount,
        currentPage: value,
      }
    }
    case ParamActions.SET_SORT_ORDER: {
      return {
        ...state,
        sortOrder: value,
      }
    }
    case ParamActions.SET_SORT_FIELD: {
      return {
        ...state,
        sortField: value,
      }
    }
    case ParamActions.SET_PAGE_TOTAL: {
      if (!state.perPage) return state
      const pageTotalCount = Math.ceil(value / state.perPage)

      return {
        ...state,
        totalCount: value,
        pageTotalCount,
        showPagination: pageTotalCount > 1,
      }
    }
    case ParamActions.SET_SORT: {
      let newSortOrder = SortOrder.desc
      if (state.sortField === value) {
        newSortOrder = state.sortOrder === SortOrder.desc ? SortOrder.asc : SortOrder.desc
      }
      return {
        ...state,
        pageCount: 0,
        currentPage: 1,
        sortField: value,
        sortOrder: newSortOrder,
      }
    }
    default:
      throw new Error('Need an action type')
  }
}

/** Track params: filters and page counts */
const useFilters = <T>(initialParams: IFiltersParams<T>) => {
  const [
    {
      currentPage,
      filters,
      pageCount,
      pageTotalCount,
      perPage,
      showPagination,
      sortField,
      sortOrder,
      totalCount,
    },
    dispatchParams,
  ] = useReducer(paramReducer, initialParams)

  // Set multiple filters replacing the existing ones
  const setAllFilters = (value: any) =>
    dispatchParams({ type: ParamActions.SET_ALL_FILTERS, value })

  // Set multiple filters
  const setFilters = (value: any) => dispatchParams({ type: ParamActions.SET_FILTERS, value })

  // Set a single filter
  const setFilter = (field: string, value?: any) =>
    dispatchParams({ type: ParamActions.SET_FILTER, field, value })

  // Clear all filters
  const clearFilters = () => dispatchParams({ type: ParamActions.RESET_FILTER })

  // Change page
  const onPageChange = (value: number) =>
    dispatchParams({ type: ParamActions.SET_PAGE_COUNT, value })

  // Track total number of pages
  const setPageTotalCount = (value: number) =>
    dispatchParams({ type: ParamActions.SET_PAGE_TOTAL, value })

  const setSort = (field: string) => {
    dispatchParams({ type: ParamActions.SET_SORT, value: field })
  }

  let setSorts = {}

  if (sortField && sortOrder) {
    setSorts = {
      sortOrder,
      sortField,
    }
  }

  const params = {
    filters,
    pageCount,
    perPage,
    ...setSorts,
  }

  return {
    currentPage,
    showPagination,
    pageCount,
    pageTotalCount,
    totalCount,
    perPage,
    filters,
    params,
    sortOrder,
    sortField,
    setFilter,
    setFilters,
    setAllFilters,
    clearFilters,
    onPageChange,
    setSort,
    setPageTotalCount,
  }
}

export default useFilters
